import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bot, MessageCircle, Send, X, Loader2, Paperclip, Plus, ChevronLeft, FileText, Image as ImageIcon, FilePlus2 } from 'lucide-react';
import axios from 'axios';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadMessagesCount } from '../../hooks/useUnreadMessagesCount';
import useChatSocket from '../../hooks/useChatSocket';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_FILE_SIZE_MB = 10;

// Routes où le FAB global est masqué (pour éviter doublon)
const HIDDEN_ROUTES = ['/messagerie', '/login', '/admin'];

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700 border-purple-200', avatar: 'bg-purple-500' },
  commercial: { label: 'Commercial', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', avatar: 'bg-emerald-500' },
  operateur: { label: 'Opérateur', color: 'bg-blue-100 text-blue-700 border-blue-200', avatar: 'bg-blue-500' },
  operator: { label: 'Opérateur', color: 'bg-blue-100 text-blue-700 border-blue-200', avatar: 'bg-blue-500' },
  field_ops: { label: 'Terrain', color: 'bg-amber-100 text-amber-700 border-amber-200', avatar: 'bg-amber-500' },
};

const initialsOf = (n) => !n ? '?' : n.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
const fmtTime = (d) => { try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const fmtDay = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return ''; } };
const fileIcon = (att) => att?.fileType === 'image' ? ImageIcon : FileText;

/**
 * GlobalChatFab — bouton chat support global accessible depuis toutes les pages client.
 * Masqué sur /messagerie (page dédiée), /login, /admin.
 * UX hybride : si 1 conv → ouverture directe ; sinon liste des conv + bouton "Nouvelle demande".
 */
export const GlobalChatFab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count: unreadCount } = useUnreadMessagesCount();

  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'conv'
  const [typingUserName, setTypingUserName] = useState(null);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingClearTimer = useRef(null);

  // Hide FAB sur certaines routes
  const shouldHide = HIDDEN_ROUTES.some(p => location.pathname.startsWith(p))
    || location.pathname.startsWith('/operations/'); // FAB op-spécifique prend le pas
  const shouldRender = !!user && (user.role === 'client' || user.role === 'expediteur') && !shouldHide;

  // ---- Fetch conversations ----
  const fetchConversations = useCallback(async () => {
    if (!user?.id) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/conversations`);
      const list = (res.data || []).map(c => ({
        ...c,
        unreadCount: c.unreadCountClient || 0,
      }));
      setConversations(list);
      // UX hybride : si 1 seule conv, l'ouvrir direct
      if (list.length === 1 && view === 'list' && !activeConvId) {
        setActiveConvId(list[0].id);
        setView('conv');
      }
    } catch (e) {
      console.error('GlobalChat fetch list error:', e);
    } finally {
      setLoadingList(false);
    }
  }, [user?.id, view, activeConvId]);

  const loadMessages = useCallback(async (convId, silent = false) => {
    if (!convId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/messages?conversation_id=${convId}`);
      const newMsgs = res.data || [];
      setMessages((prev) => {
        if (prev.length !== newMsgs.length) return newMsgs;
        const lp = prev[prev.length - 1], ln = newMsgs[newMsgs.length - 1];
        if (!lp || !ln) return newMsgs;
        return (lp.id === ln.id && (lp.content || '') === (ln.content || '')) ? prev : newMsgs;
      });
    } catch (e) {
      if (!silent) console.error(e);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchConversations();
  }, [open, fetchConversations]);

  useEffect(() => {
    if (activeConvId && view === 'conv') {
      loadMessages(activeConvId, false);
      const id = setInterval(() => loadMessages(activeConvId, true), 15000);
      return () => clearInterval(id);
    }
  }, [activeConvId, view, loadMessages]);

  // WebSocket
  const { lastMessage: wsMsg, sendTyping } = useChatSocket(view === 'conv' ? activeConvId : null, user?.id, user?.role);
  useEffect(() => {
    if (!wsMsg) return;
    if (wsMsg.type === 'new_message' || wsMsg.type === 'conversation_updated') {
      if (activeConvId) loadMessages(activeConvId, true);
      fetchConversations();
      setTypingUserName(null);
    } else if (wsMsg.type === 'user_typing' && wsMsg.user_id !== user?.id) {
      if (wsMsg.is_typing) {
        const lastFromUser = [...messages].reverse().find(m => m.senderId === wsMsg.user_id);
        setTypingUserName(lastFromUser?.senderName || 'Support Baobab');
        if (typingClearTimer.current) clearTimeout(typingClearTimer.current);
        typingClearTimer.current = setTimeout(() => setTypingUserName(null), 4000);
      } else {
        setTypingUserName(null);
      }
    }
  }, [wsMsg, activeConvId, user?.id, messages, loadMessages, fetchConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---- Actions ----
  const openConv = (convId) => { setActiveConvId(convId); setView('conv'); };
  const backToList = () => { setActiveConvId(null); setMessages([]); setView('list'); };
  const startNewConversation = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/conversations`, {
        type: 'GENERAL',
        clientId: user?.id,
        message: 'Bonjour, j\'ai une question pour le support.',
      });
      await fetchConversations();
      setActiveConvId(res.data.id);
      setView('conv');
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de créer la conversation', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast({ title: 'Fichier rejeté', description: `${f.name} : type non supporté`, variant: 'destructive' });
        return false;
      }
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast({ title: 'Fichier trop volumineux', description: `${f.name} > ${MAX_FILE_SIZE_MB}MB`, variant: 'destructive' });
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async () => {
    if ((!text.trim() && files.length === 0) || !activeConvId || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('content', text.trim() || '(Pièce jointe)');
      fd.append('conversation_id', activeConvId);
      files.forEach(f => fd.append('files', f));
      await axios.post(`${API}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setText('');
      setFiles([]);
      await loadMessages(activeConvId, true);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Envoi impossible', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (!shouldRender) return null;

  const activeConv = conversations.find(c => c.id === activeConvId);

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(true)}
        title="Discuter avec le support"
        className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center"
        data-testid="global-chat-fab"
      >
        <Bot className="w-6 h-6" />
        {/* Pastille statut online */}
        <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-emerald-400 border-2 border-white rounded-full" />
        {/* Badge unread */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -left-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="global-chat-panel">
          <VisuallyHidden>
            <SheetTitle>Support Baobab</SheetTitle>
          </VisuallyHidden>

          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-5 py-4 flex items-center gap-3">
            {view === 'conv' && (
              <button onClick={backToList} className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">Support Baobab</p>
              <p className="text-xs text-emerald-100 truncate flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full" />
                {view === 'conv' ? (activeConv?.subject || 'Conversation en cours') : 'En ligne · réponse rapide'}
              </p>
            </div>
            {view === 'conv' && activeConvId && (
              <button
                onClick={() => { setOpen(false); navigate(`/nouvelle-demande?from_conv=${activeConvId}`); }}
                className="text-white/90 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/15 flex items-center gap-1.5 text-xs font-medium border border-white/20"
                data-testid="global-chat-quote-btn"
                title="Créer une demande de transport pré-remplie avec le contexte"
              >
                <FilePlus2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Devis</span>
              </button>
            )}
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* CONTENT — LIST view */}
          {view === 'list' && (
            <div className="flex-1 overflow-y-auto bg-slate-50">
              {loadingList && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                </div>
              )}

              {!loadingList && conversations.length === 0 && (
                <div className="text-center px-6 py-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                    <MessageCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-sm font-medium text-slate-800 mb-1">Aucune conversation</p>
                  <p className="text-xs text-slate-500 mb-5">Démarrez une discussion avec notre équipe support.</p>
                  <Button
                    onClick={startNewConversation}
                    disabled={sending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="start-new-conversation-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Démarrer une conversation
                  </Button>
                </div>
              )}

              {!loadingList && conversations.length > 0 && (
                <ul className="divide-y divide-slate-100" data-testid="conversations-list">
                  {conversations.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => openConv(c.id)}
                        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-white transition-colors text-left"
                        data-testid={`conv-item-${c.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <Bot className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-800 truncate">
                              {c.subject || 'Conversation'}
                            </p>
                            <span className="text-[10px] text-slate-400 shrink-0">{fmtDay(c.lastMessageAt || c.createdAt)}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate mt-0.5">{c.lastMessagePreview || '—'}</p>
                        </div>
                        {c.unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center shrink-0">
                            {c.unreadCount}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Footer actions list view */}
              {!loadingList && conversations.length > 0 && (
                <div className="p-3 border-t border-slate-200 bg-white">
                  <Button
                    onClick={startNewConversation}
                    disabled={sending}
                    variant="outline"
                    className="w-full gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid="start-new-conversation-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Nouvelle demande
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* CONTENT — CONV view */}
          {view === 'conv' && (
            <>
              <div className="flex-1 overflow-y-auto bg-slate-50 px-4 py-4 space-y-3" data-testid="global-chat-messages">
                {loadingMessages && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                  </div>
                )}

                {!loadingMessages && messages.map((msg) => {
                  const isMine = msg.senderId === user?.id || msg.senderType === 'client';
                  const role = ROLE_CONFIG[(msg.senderType || '').toLowerCase()];
                  const senderName = msg.senderName || (isMine ? 'Vous' : 'Support Baobab');
                  return (
                    <div key={msg.id} className={cn('flex gap-2.5', isMine ? 'flex-row-reverse' : 'flex-row')}>
                      {!isMine && (
                        <Avatar className={cn('w-8 h-8 shrink-0', role?.avatar || 'bg-slate-500')}>
                          <AvatarFallback className={cn('text-white text-xs font-semibold', role?.avatar || 'bg-slate-500')}>
                            {initialsOf(senderName)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn('max-w-[78%] flex flex-col', isMine ? 'items-end' : 'items-start')}>
                        {!isMine && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs font-semibold text-slate-700">{senderName}</span>
                            {role && (
                              <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium', role.color)}>
                                {role.label}
                              </span>
                            )}
                          </div>
                        )}
                        <div className={cn(
                          'px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words',
                          isMine ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                        )}>
                          {msg.content}
                        </div>
                        {/* Attachments */}
                        {msg.attachments?.length > 0 && (
                          <div className="mt-1.5 flex flex-col gap-1">
                            {msg.attachments.map((att) => {
                              const Icon = fileIcon(att);
                              return (
                                <a
                                  key={att.id}
                                  href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 underline"
                                >
                                  <Icon className="w-3.5 h-3.5" />
                                  <span className="truncate max-w-[180px]">{att.filename}</span>
                                </a>
                              );
                            })}
                          </div>
                        )}
                        <span className="text-[10px] text-slate-400 mt-1">{fmtTime(msg.createdAt || msg.timestamp)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-slate-200 bg-white p-3">
                {typingUserName && (
                  <div className="flex items-center gap-2 px-1 pb-2 text-xs text-slate-500 italic" data-testid="global-chat-typing">
                    <span className="flex gap-0.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    <span>{typingUserName} est en train d'écrire…</span>
                  </div>
                )}

                {files.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {files.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-slate-100 rounded-md px-2 py-1 text-xs">
                        <FileText className="w-3 h-3 text-slate-500" />
                        <span className="truncate max-w-[120px]">{f.name}</span>
                        <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-slate-400 hover:text-emerald-600 p-2 rounded-md hover:bg-emerald-50 transition-colors shrink-0"
                    data-testid="global-chat-attach-btn"
                    title="Joindre un fichier"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="global-chat-file-input"
                  />
                  <textarea
                    value={text}
                    onChange={(e) => { setText(e.target.value); sendTyping && sendTyping(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Votre message…"
                    rows={1}
                    className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 max-h-32"
                    data-testid="global-chat-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={(!text.trim() && files.length === 0) || sending || !activeConvId}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 h-10 w-10 p-0 rounded-lg shrink-0"
                    data-testid="global-chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default GlobalChatFab;
